/**
 * Canonical file path values we store for virtual/special scan types.
 * Use these when normalizing paths for storage and deduplication so frontend,
 * extensions, and backend stay consistent. Change display text via
 * FILE_PATH_DISPLAY_LABELS only — never store display strings in the DB.
 */
export const STORED_FILE_PATHS = {
    VSCodeCopilotChat: 'VSCode Copilot Chat',
    CursorDbStateVscdb: 'Cursor DB (state.vscdb)',
    InstalledExtensions: 'Installed Extensions',
};
// Raw SettingsMonitor URI persisted as `file_ownership.file_path`. Display
// label resolves via FILE_PATH_DISPLAY_LABELS. KIR-2595.
export const VSCODE_SETTINGS_FILE_PATH = 'vscode://settings';
// Synthetic scheme the VS Code-family extensions store for hooks declared
// inline in an IDE settings.json block — e.g. `vscode-settings://kirin.hooks`,
// `vscode-settings://cursor.hooks` (see hookInventory.ts in
// extensions/vscode-core). The authority segment is the settings
// configuration key the hook block came from. There is one such path per
// `<namespace>.hooks` config key, so the label/details are derived from the
// scheme rather than enumerated in a static map. KIR-2917.
export const VSCODE_SETTINGS_HOOK_PREFIX = 'vscode-settings://';
/**
 * Map from stored file path values to display labels for UI and notifications.
 * Stored values stay stable; only what we show in notifications and tables
 * is configured here. Add or edit entries to change display without code changes.
 */
export const FILE_PATH_DISPLAY_LABELS = {
    [STORED_FILE_PATHS.VSCodeCopilotChat]: STORED_FILE_PATHS.VSCodeCopilotChat,
    [STORED_FILE_PATHS.CursorDbStateVscdb]: STORED_FILE_PATHS.CursorDbStateVscdb,
    [STORED_FILE_PATHS.InstalledExtensions]: STORED_FILE_PATHS.InstalledExtensions,
    [VSCODE_SETTINGS_FILE_PATH]: 'VS Code Agent Settings',
    // Hook-based synthetic file paths → user-friendly labels
    'hook:beforeSubmitPrompt': 'Chat',
    'hook:UserPromptSubmit': 'Chat',
    'hook:beforeTabFileRead': 'File Read',
    'hook:beforeReadFile': 'File Read',
    // KIR-3765: distinguish tool input (pre-execution) from tool output
    // (post-execution) so secret alerts read which side leaked the secret.
    'hook:PreToolUse': 'Tool Input',
    'hook:preToolUse': 'Tool Input',
    'hook:PostToolUse': 'Tool Output',
    'hook:beforeShellExecution': 'Shell Execution',
    'User prompt': 'Chat',
    'user-prompt': 'Chat',
};
/**
 * Returns the display label for a file path. Uses FILE_PATH_DISPLAY_LABELS when
 * the path has a known entry; otherwise returns the path as-is.
 */
export function getFilePathDisplayLabel(filePath) {
    if (filePath == null || filePath === '') {
        return '—';
    }
    const trimmed = filePath.trim();
    if (trimmed === '') {
        return '—';
    }
    if (trimmed in FILE_PATH_DISPLAY_LABELS) {
        return FILE_PATH_DISPLAY_LABELS[trimmed];
    }
    const hookLabel = vscodeSettingsHookLabel(trimmed);
    if (hookLabel !== null) {
        return hookLabel;
    }
    // KIR-3157: mcp-tool-response scan paths are namespaced
    // `mcp-tool-response:<file-or-command>` so they never collide with an ordinary
    // bare path in the content+filePath scan dedup. Strip the prefix for display
    // so the alert "File" column shows the file read / command run.
    const mcpPrefix = 'mcp-tool-response:';
    if (trimmed.startsWith(mcpPrefix)) {
        const rest = trimmed.slice(mcpPrefix.length);
        return rest !== '' ? rest : trimmed;
    }
    return filePath;
}
/**
 * Heuristic check for a real file system path: absolute POSIX (`/`),
 * home-relative (`~`), or Windows drive-letter (`C:\`). Mirrors the backend
 * `isFilePath` in `domain/findings/finding-identity.ts` so the frontend and
 * backend agree on what counts as a real path.
 */
function isRealFilePath(value) {
    return /^(?:\/|~|[A-Za-z]:[/\\])/.test(value);
}
/**
 * Synthetic per-event sentinels the agent / extensions store in `filePath`
 * when there is no single backing file — `hook:<event>`,
 * `platform-secret:<platform>:<event>`, `platform-chat:<platform>:<event>`,
 * `ide-state:<platform>:<event>`, and the bare prompt markers. These are
 * stable dedup keys, not human-readable names; we translate them at render
 * time. Distinct from the virtual *aggregate* labels in
 * FILE_PATH_DISPLAY_LABELS (e.g. "Installed Extensions"), which already read
 * well and should never be swapped for a single location.
 */
function isSyntheticEventSentinel(value) {
    return (value.startsWith('hook:') ||
        value.startsWith('platform-secret:') ||
        value.startsWith('platform-chat:') ||
        value.startsWith('ide-state:') ||
        value === 'user-prompt' ||
        value === 'User prompt');
}
/** Friendly labels for known hook / platform event names. */
const EVENT_LABELS = {
    // KIR-3765: pre-tool events carry the tool INPUT, post-tool events carry the
    // tool OUTPUT — labeling them distinctly lets a secret alert show which side
    // of a tool call leaked the secret.
    PreToolUse: 'Tool Input',
    preToolUse: 'Tool Input',
    PostToolUse: 'Tool Output',
    beforeShellExecution: 'Shell Execution',
    beforeSubmitPrompt: 'Chat',
    UserPromptSubmit: 'Chat',
    // GitHub Copilot CLI hook events (KIR-3179). preToolUse is already mapped
    // above; postToolUse carries the tool OUTPUT (mirrors PostToolUse); the rest
    // are its prompt/session/turn-end analogs, mapped to "Chat" so a
    // platform-secret / ide-state alert reads "Chat"/"Tool Output" instead of the
    // raw hook name (e.g. "User Prompt Submitted").
    postToolUse: 'Tool Output',
    'hook:postToolUse': 'Tool Output',
    userPromptSubmitted: 'Chat',
    sessionStart: 'Chat',
    agentStop: 'Chat',
    beforeTabFileRead: 'File Read',
    beforeReadFile: 'File Read',
    // Gemini CLI hook events (KIR-3178). Mapped to the same activity labels as
    // their Claude Code equivalents so a platform-secret/ide-state alert reads
    // "Chat"/"Tool Input"/"Tool Output" instead of the raw hook name.
    BeforeAgent: 'Chat', // prompt submission (carries the user prompt)
    AfterAgent: 'Chat', // final assistant output
    BeforeModel: 'Chat',
    AfterModel: 'Chat',
    BeforeTool: 'Tool Input',
    AfterTool: 'Tool Output',
    BeforeToolSelection: 'Tool Use',
    // Windsurf / Cascade hook events (KIR-3873). Devin Desktop runs two agents
    // behind one platform: Devin Local reuses Claude's PascalCase names, already
    // mapped above, but Cascade's are snake_case and had no entries — so a
    // Cascade alert rendered a title-cased hook name ("Pre Run Command") where
    // every other platform shows the activity.
    pre_run_command: 'Shell Execution',
    pre_write_code: 'File Write',
    pre_read_code: 'File Read',
    pre_user_prompt: 'Chat',
    pre_mcp_tool_use: 'Tool Input',
    post_mcp_tool_use: 'Tool Output',
    // Claude Code / Devin Local turn-end event — the assistant's final output,
    // same activity as Copilot CLI's agentStop.
    Stop: 'Chat',
};
// Raw hook/platform event names whose payload is a tool INPUT / OUTPUT, across
// platforms (Claude Code `PreToolUse`/`PostToolUse`, Gemini CLI
// `BeforeTool`/`AfterTool`). Keep in lockstep with the `Tool Input`/`Tool
// Output` entries in EVENT_LABELS.
const PRE_TOOL_EVENT_NAMES = new Set([
    'PreToolUse',
    'preToolUse',
    'BeforeTool',
    'pre_mcp_tool_use',
]);
const POST_TOOL_EVENT_NAMES = new Set([
    'PostToolUse',
    'postToolUse',
    'AfterTool',
    'post_mcp_tool_use',
]);
/** Display labels for a {@link ToolEventKind}. */
export const TOOL_EVENT_KIND_LABELS = {
    pre: 'Tool Input',
    post: 'Tool Output',
};
/**
 * Map a raw hook/platform event name to its {@link ToolEventKind}, or
 * `undefined` for non-tool events. Used by the backend at scan-ingest to derive
 * the first-class `eventKind` persisted on secret findings.
 */
export function resolveToolEventKind(eventName) {
    if (!eventName) {
        return undefined;
    }
    const token = eventName.trim();
    if (PRE_TOOL_EVENT_NAMES.has(token)) {
        return 'pre';
    }
    if (POST_TOOL_EVENT_NAMES.has(token)) {
        return 'post';
    }
    return undefined;
}
/**
 * Extract the trailing event token from a synthetic sentinel (see
 * {@link isSyntheticEventSentinel}): `hook:<event>`,
 * `platform-secret:<platform>:<event>`, `platform-chat:<platform>:<event>`,
 * `ide-state:<platform>:<event>`. Returns `''` when `filePath` is not a
 * recognized sentinel. Centralizes sentinel-format knowledge so callers that
 * need the raw event (e.g. the backend `eventKind` fallback) don't re-implement
 * the parse.
 */
export function eventTokenFromSentinel(filePath) {
    if (filePath == null) {
        return '';
    }
    const value = filePath.trim();
    if (!isSyntheticEventSentinel(value)) {
        return '';
    }
    const segments = value.split(':');
    const prefix = segments[0];
    if (prefix === 'hook') {
        return segments.slice(1).join(':');
    }
    // platform-secret:<platform>:<event> / ide-state:<platform>:<event> /
    // platform-chat:<platform>:<event>
    return segments.length >= 3
        ? segments.slice(2).join(':')
        : (segments[1] ?? '');
}
/** Title-case an underscore/hyphen-delimited token: `foo_bar` → `Foo Bar`. */
function titleCase(token) {
    return token
        .split(/[_-]+/)
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}
/**
 * Describe an MCP tool token `mcp__<server>__<tool>` as `Server: Tool`
 * (e.g. `mcp__workspace__bash` → `Workspace: Bash`). Returns null when the
 * token is not in MCP tool shape.
 */
function describeMcpTool(token) {
    const parts = token.split('__').filter(Boolean);
    if (parts.length >= 3 && parts[0] === 'mcp') {
        return `${titleCase(parts[1])}: ${parts.slice(2).map(titleCase).join(' ')}`;
    }
    return null;
}
/** Translate a single hook/platform event token into a friendly label. */
function describeEventToken(token) {
    if (token === '') {
        return 'Activity';
    }
    if (token in EVENT_LABELS) {
        return EVENT_LABELS[token];
    }
    return describeMcpTool(token) ?? titleCase(token);
}
/**
 * Translate a synthetic event sentinel (see {@link isSyntheticEventSentinel})
 * into a human-readable description of the command / tool / activity.
 */
function describeSyntheticFilePath(value) {
    if (value in FILE_PATH_DISPLAY_LABELS) {
        return FILE_PATH_DISPLAY_LABELS[value];
    }
    const segments = value.split(':');
    const prefix = segments[0];
    if (prefix === 'platform-chat') {
        return 'Chat';
    }
    if (prefix === 'hook') {
        return describeEventToken(segments.slice(1).join(':'));
    }
    // platform-secret:<platform>:<event> / ide-state:<platform>:<event>
    const eventToken = segments.length >= 3 ? segments.slice(2).join(':') : (segments[1] ?? '');
    return describeEventToken(eventToken);
}
/**
 * Canonical MCP gateway hook surfaces (KIR-3287). Single source of truth for
 * valid `mcp:<surface>:<tool>` surface tokens — both the File-column label
 * table below (MCP_SURFACE_LABELS) and the detail-card description sections
 * (MCP_SURFACE_SECTIONS in helpers/alerts.ts) key off this type so the two
 * can't silently diverge on which surfaces are known.
 *
 * Producer side is `REPORT_FILE_TYPE_*` in the inspector plugin's `plugin.py`
 * (kirin `extensions/contextforge/`). A new `contextforge-<surface>` file_type
 * must be added here too, or the dashboard silently degrades to raw sentinel
 * text — nothing cross-language catches the drift.
 */
export const MCP_GATEWAY_SURFACES = [
    'tool-args',
    'tool-result',
    'prompt-args',
    'prompt-result',
    'tool-description',
];
/** Runtime allowlist for {@link McpGatewaySurface} — narrows a raw sentinel token. */
export function isMcpGatewaySurface(value) {
    return MCP_GATEWAY_SURFACES.includes(value);
}
/**
 * MCP gateway hook surfaces. The ContextForge inspector plugin packs these into
 * the `filePath` sentinel as `mcp:<surface>:<tool>` (KIR-3287) so the alerts
 * dashboard can show which hook surface caught the secret — the same way the
 * agent/IDE hooks encode their event in `hook:<event>` — with no new field.
 */
const MCP_SURFACE_LABELS = {
    'tool-args': 'Tool argument',
    'tool-result': 'Tool result',
    'prompt-args': 'Prompt argument',
    'prompt-result': 'Prompt render',
    'tool-description': 'Tool description',
};
/** Shown for a gateway sentinel that carries neither surface nor tool (`mcp:`). */
const MCP_GATEWAY_GENERIC_LABEL = 'MCP gateway';
/**
 * Parse the gateway's `mcp:` filePath sentinel. Single source of truth for the
 * `mcp:<surface>:<tool>` / legacy `mcp:<tool>` split — both the File-column
 * label ({@link describeMcpGatewayPath}) and the detail-card description
 * sections (`formatMcpGatewaySections` in helpers/alerts.ts) derive from this
 * so the two consumers can't drift apart on the sentinel format. Returns null
 * when `filePath` is not an `mcp:` sentinel.
 */
export function parseMcpGatewaySentinel(filePath) {
    if (filePath == null || !filePath.startsWith('mcp:')) {
        return null;
    }
    const rest = filePath.slice('mcp:'.length);
    const sep = rest.indexOf(':');
    if (sep === -1) {
        // Legacy `mcp:<tool>` (pre-KIR-3287): no surface segment.
        return { surface: '', tool: rest };
    }
    const head = rest.slice(0, sep);
    if (isMcpGatewaySurface(head)) {
        return { surface: head, tool: rest.slice(sep + 1) };
    }
    // The surface segment is an allowlist match, not "whatever precedes the first
    // colon": `tool_name` is the gateway's `payload.name` and kirin never
    // constrains its character set, so a colon-bearing tool name must stay a tool
    // name rather than be silently reinterpreted as a surface.
    return { surface: '', tool: head === '' ? rest.slice(sep + 1) : rest };
}
/**
 * Translate the gateway's `mcp:` filePath sentinel into a friendly label.
 * Returns null when `filePath` is not an `mcp:` sentinel (caller falls through).
 *
 *   `mcp:tool-args:server-a-echo` → "Tool argument — server-a-echo"
 *   `mcp:server-a-echo` (legacy, no surface) → "server-a-echo"
 *   `mcp:` (no surface, no tool) → "MCP gateway"
 *
 * An unknown surface token is treated as part of the tool name (see
 * {@link parseMcpGatewaySentinel}), so it is shown whole rather than split.
 */
function describeMcpGatewayPath(filePath) {
    const parsed = parseMcpGatewaySentinel(filePath);
    if (!parsed) {
        return null;
    }
    const { surface, tool } = parsed;
    if (surface === '') {
        // Never return null here: the caller would fall through to the raw-label
        // path and render the internal `mcp:` string that KIR-3154 set out to hide.
        return tool || MCP_GATEWAY_GENERIC_LABEL;
    }
    const label = MCP_SURFACE_LABELS[surface];
    return tool ? `${label} — ${tool}` : label;
}
/**
 * Resolve the human-readable label for an alert's "File" column (KIR-3154).
 *
 * Order:
 *  1. A real path in `filePath` is shown verbatim.
 *  2. For a synthetic event sentinel (or empty `filePath`), the real file
 *     `location` is preferred when one is known.
 *  3. Otherwise a synthetic sentinel is translated to a friendly description
 *     (e.g. `hook:mcp__workspace__bash` → "Workspace: Bash"); known aggregate
 *     labels fall through to {@link getFilePathDisplayLabel}.
 */
export function getAlertFileDisplayLabel(input) {
    const filePath = (input.filePath ?? '').trim();
    const location = (input.location ?? '').trim();
    // ContextForge gateway surface (KIR-3287): the plugin packs the hook surface
    // into the `mcp:<surface>:<tool>` filePath sentinel. Translate it to
    // "<surface> — <tool>" so the File column reads like the agent/IDE hooks.
    const mcpLabel = describeMcpGatewayPath(filePath);
    if (mcpLabel) {
        return mcpLabel;
    }
    if (filePath !== '' && isRealFilePath(filePath)) {
        return filePath;
    }
    if ((filePath === '' || isSyntheticEventSentinel(filePath)) &&
        location !== '' &&
        isRealFilePath(location)) {
        return location;
    }
    if (filePath !== '' && isSyntheticEventSentinel(filePath)) {
        // KIR-3765: prefer the first-class eventKind over sentinel parsing for the
        // Tool Input/Output distinction; fall back to the sentinel when it's absent.
        if (input.eventKind) {
            return TOOL_EVENT_KIND_LABELS[input.eventKind];
        }
        return describeSyntheticFilePath(filePath);
    }
    if (filePath !== '') {
        return getFilePathDisplayLabel(filePath);
    }
    if (location !== '') {
        // filePath is empty here. If location is itself a synthetic sentinel
        // (e.g. hook:mcp__workspace__bash when only the occurrence location was
        // populated), translate it rather than echoing the raw internal string
        // KIR-3154 set out to hide.
        if (isSyntheticEventSentinel(location)) {
            if (input.eventKind) {
                return TOOL_EVENT_KIND_LABELS[input.eventKind];
            }
            return describeSyntheticFilePath(location);
        }
        return getFilePathDisplayLabel(location);
    }
    return '—';
}
// Add new virtual configuration sources here — no renderer changes needed.
export const VIRTUAL_PATH_DETAILS = {
    [VSCODE_SETTINGS_FILE_PATH]: {
        source: VSCODE_SETTINGS_FILE_PATH,
        backedBy: 'settings.json (~/Library/Application Support/Code/User/settings.json on macOS, ~/.config/Code/User/settings.json on Linux, %APPDATA%\\Code\\User\\settings.json on Windows)',
    },
};
/**
 * Describe a `vscode-settings://<configKey>` hook path (see
 * {@link VSCODE_SETTINGS_HOOK_PREFIX}). `<configKey>` is the settings.json
 * block the hooks were declared under (`kirin.hooks`, `cursor.hooks`, …).
 * Returns null for anything that is not this scheme so callers fall back to
 * the static map / verbatim rendering.
 */
function describeVscodeSettingsHookPath(filePath) {
    if (!filePath.startsWith(VSCODE_SETTINGS_HOOK_PREFIX)) {
        return null;
    }
    const configKey = filePath.slice(VSCODE_SETTINGS_HOOK_PREFIX.length);
    return {
        source: filePath,
        backedBy: configKey
            ? `IDE settings.json — "${configKey}" configuration block`
            : 'IDE settings.json configuration block',
    };
}
/**
 * Friendly label for a `vscode-settings://<namespace>.hooks` path, e.g.
 * `vscode-settings://kirin.hooks` → `Kirin Settings Hooks`. Returns null when
 * the path is not in this scheme.
 */
function vscodeSettingsHookLabel(filePath) {
    if (!filePath.startsWith(VSCODE_SETTINGS_HOOK_PREFIX)) {
        return null;
    }
    const configKey = filePath.slice(VSCODE_SETTINGS_HOOK_PREFIX.length);
    const namespace = configKey.replace(/\.hooks$/i, '');
    const friendly = namespace ? titleCase(namespace) : 'VS Code';
    return `${friendly} Settings Hooks`;
}
// Null for real workspace paths; caller renders them verbatim.
export function getVirtualPathDetails(filePath) {
    if (filePath == null || filePath === '') {
        return null;
    }
    const trimmed = filePath.trim();
    if (trimmed === '') {
        return null;
    }
    return (VIRTUAL_PATH_DETAILS[trimmed] ??
        describeVscodeSettingsHookPath(trimmed) ??
        null);
}
//# sourceMappingURL=file-path-labels.js.map